import { Injectable, OnDestroy, inject } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { BehaviorSubject, Subject, filter, takeUntil } from 'rxjs';
import { ProtectedRoute, protectedRoutes } from '../protectedRouteConfig';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  user$ = new BehaviorSubject<AccountInfo | null>(null);
  /**
   * pages$ contains the available authenticated pages for the
   * current user based on their role.
   */
  routes$ = new BehaviorSubject<Record<string, ProtectedRoute>>({});
  ngUnsubscribe = new Subject<void>();

  msalService = inject(MsalService);

  constructor() {
    inject(MsalBroadcastService)
      .inProgress$.pipe(
        /**
         * The "none" status indicates that the auth is ready.
         * There's usually a couple other events fired before this
         * like "startup" and "handleRedirect". In this context
         * we can ignore those.
         */
        filter((status) => status === InteractionStatus.None),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.updateUserAndRoutes();
      });
  }

  private updateUserAndRoutes() {
    const accounts = this.msalService.instance.getAllAccounts();
    const user = accounts?.[0];
    
    // Ensure active account is set if we have accounts but no active account
    if (user && !this.msalService.instance.getActiveAccount()) {
      this.msalService.instance.setActiveAccount(user);
    }
    
    let routes: Record<string, ProtectedRoute> = {};

    if (user && user.idTokenClaims?.roles) {
      for (const [routeId, route] of Object.entries(protectedRoutes)) {
        if (
          user?.idTokenClaims?.roles?.some((role) =>
            route.roles.includes(role)
          )
        ) {
          routes[routeId] = route;
        }
      }
    }

    this.user$.next(user);
    this.routes$.next(routes);
  }

  ngOnDestroy() {
    // _probably_ not needed in a service, but it's good practice
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  getRoles(): string[] {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) return [];

    try {
      return account.idTokenClaims?.roles || [];
    } catch (error) {
      console.error('Error decoding token:', error);
      return [];
    }
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }
}
